/**
 * Session tracer — records an ordered page/screen/click stream for one sitting
 * and flushes it to the backend in batches.
 *
 * A module singleton rather than a hook, because the buffer and the session id
 * must outlive any component and be reachable from non-React call sites.
 *
 * Three things are worth understanding before changing this:
 *
 * 1. **Dwell.** The event the user is currently *on* is held in `openEvent`,
 *    not the buffer — its `dwellMs` isn't known until the next view supersedes
 *    it. So a flush never ships an event with an unknown dwell. The cost is
 *    that a hard browser kill loses the trailing event.
 *
 * 2. **Visibility.** On `visibilitychange → hidden` (tab switch, mobile
 *    backgrounding) we close the open event and flush, because we may never be
 *    called again. On the way back we re-open the same page as a new view —
 *    which is honest: the user did look at it again, and the timestamp gap
 *    shows the absence.
 *
 * 3. **Transport.** `fetch(..., { keepalive: true })`, never `sendBeacon` —
 *    beacons can't carry an `Authorization` header and this endpoint is
 *    user-authed. Failures are swallowed; instrumentation must never break a
 *    flow.
 *
 * See docs/FeatureDesigns/user-actions.md.
 */

import type {
  SessionActivityEntry,
  SessionEndedReason,
  SessionState,
  UserActionBatch,
  UserActionEvent,
  UserActionKind,
  UserActionProps,
} from './types';
import { setCurrentSessionId } from './sessionContext';

const STORAGE_KEY = 'user_actions:session';

/** A gap longer than this starts a new session. */
export const IDLE_MS = 30 * 60 * 1000;
/** Flush once this many closed events have accumulated. */
export const FLUSH_AT_EVENTS = 20;
/** Periodic flush so a slow browser still ships events. */
export const FLUSH_INTERVAL_MS = 30_000;
/** Matches the server's per-session cap; past it we stop sending. */
const MAX_BATCHES = 500;
/** Matches the server's label cap. */
const MAX_ACTION_TARGET_CHARS = 120;
/** Distinct surfaces tallied per session. Past it the counts keep running for
 *  surfaces already seen, and the session is flagged truncated. */
export const MAX_ACTIVITY_ENTRIES = 100;

export interface UserActionConfig {
  appSource: string;
  apiBase: string;
  getAuthHeaders: () => HeadersInit;
  appVersion?: string;
}

// ── Module state ────────────────────────────────────────────────────────

let config: UserActionConfig | null = null;
let userId: string | null = null;
/** Gate + server kill switch. Nothing is recorded or sent while false. */
let active = false;

let session: SessionState | null = null;
/**
 * Assign the sitting, and mirror its id where the API client can reach it.
 *
 * Every assignment goes through here, so a sitting that starts, rolls over or
 * is dropped cannot leave a stale id behind for server-side acts to attach to.
 */
function setSession(next: SessionState | null): void {
  session = next;
  setCurrentSessionId(next?.id);
}
let buffer: UserActionEvent[] = [];
let openEvent: UserActionEvent | null = null;
let openedAtMs = 0;
/** Last page label seen, so screen events can attach to their page. */
let currentPage = '';

let flushTimer: ReturnType<typeof setInterval> | null = null;
let listenersBound = false;

function nowIso(): string {
  return new Date().toISOString();
}

function newSessionId(): string {
  // 24 chars from the URL-safe alphabet the server regex accepts.
  const bytes = new Uint8Array(18);
  crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// ── Session state ───────────────────────────────────────────────────────

function readState(): SessionState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SessionState;
    if (!parsed?.id || !parsed.startedAt || !parsed.lastActivityAt) return null;
    // State written before the tally existed, or corrupted — keep the session,
    // start the tally from here rather than dropping the sitting.
    if (!Array.isArray(parsed.activity)) parsed.activity = [];
    return parsed;
  } catch {
    return null;
  }
}

function writeState(state: SessionState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* private mode / quota — tracing degrades to per-page-load sessions */
  }
}

function clearState(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function startSession(uid: string): SessionState {
  const t = nowIso();
  const state: SessionState = { id: newSessionId(), startedAt: t, lastActivityAt: t, userId: uid, activity: [] };
  writeState(state);
  setSession(state);
  buffer = [];
  openEvent = null;
  return state;
}

type Counter = 'nextSeq' | 'nextBatchSeq';

/**
 * A per-session counter's current value, caught up with storage.
 *
 * The counters live in the persisted session rather than module state because
 * a reload — or a second tab — resumes the same sitting. Restarting them at 0
 * there made the new page load overwrite the earlier one's event batches (the
 * server keys them `e:<sittingId>:<batchSeq>`, and treats batch 0 as the
 * sitting's start) and interleave its `seq`s with theirs. Storage is re-read so
 * another tab's allocations count too.
 */
function peekCounter(field: Counter): number {
  if (!session) return 0;
  const stored = readState();
  const n = Math.max(session[field] ?? 0, stored?.id === session.id ? stored[field] ?? 0 : 0);
  session[field] = n;
  return n;
}

/** Take the next value of a per-session counter. */
function take(field: Counter): number {
  const n = peekCounter(field);
  if (session) {
    session[field] = n + 1;
    writeState(session);
  }
  return n;
}

/**
 * Resolve the session to record into, rolling over on idle or identity change.
 * Rolling over flushes whatever the previous session had pending, tagged with
 * why it ended.
 */
function ensureSession(uid: string): SessionState {
  const stored = session ?? readState();

  if (stored && stored.userId === uid) {
    const idleFor = Date.now() - Date.parse(stored.lastActivityAt);
    if (idleFor <= IDLE_MS) {
      setSession(stored);
      return stored;
    }
    // Same user, just a gap — safe to ship what's pending under their token.
    endSession('idle');
    return startSession(uid);
  }

  if (stored) {
    // Different user on this device. Discard rather than flush: the buffered
    // events belong to the previous user, but the auth header we'd send now
    // carries the *new* user's token, and the server attributes by token — so
    // flushing here would file one person's activity under another's id.
    // The deliberate logout path calls resetSession() while the old token is
    // still valid, which is where those events actually get shipped.
    discardSession();
  }
  return startSession(uid);
}

function touchActivity(): void {
  if (!session) return;
  session.lastActivityAt = nowIso();
  writeState(session);
}

// ── Activity tally ──────────────────────────────────────────────────────
//
// The session's own running summary: one row per distinct surface touched.
// It rides in the persisted session state (so a reload keeps counting) and is
// sent whole on every flush, which is what lets the server `set` it in a single
// blind patch op — see docs/FeatureDesigns/user-activity-analytics.md.

/** A click's identity is its target; a view's is its page (+ screen). */
function targetOf(ev: UserActionEvent): string | undefined {
  const t = ev.props?.target;
  return ev.kind === 'click' && typeof t === 'string' ? t : undefined;
}

function sameSurface(e: SessionActivityEntry, kind: UserActionKind, page: string, screen?: string, target?: string): boolean {
  return e.kind === kind && e.page === page && e.screen === screen && e.target === target;
}

/**
 * Count one event against its surface, at *record* time rather than at close
 * time — so a session that ends abruptly still reports the visit, and only its
 * trailing dwell is lost.
 */
function tallyEvent(ev: UserActionEvent): void {
  if (!session) return;
  if (!session.activity) session.activity = [];
  const entries = session.activity;
  const target = targetOf(ev);

  const entityId = typeof ev.props?.questionId === 'string' ? ev.props.questionId : undefined;
  const found = entries.find(e => sameSurface(e, ev.kind, ev.page, ev.screen, target));
  if (found) {
    found.n++;
    if (entityId) countEntity(found, entityId);
    return;
  }
  if (entries.length >= MAX_ACTIVITY_ENTRIES) {
    session.activityTruncated = true;
    return;
  }
  entries.push({
    kind: ev.kind,
    page: ev.page,
    ...(ev.screen ? { screen: ev.screen } : {}),
    ...(target ? { target } : {}),
    n: 1,
    firstSeq: ev.seq,
    ...(entityId ? { entities: { [entityId]: 1 } } : {}),
  });
}

/** Which things this surface touched, counted — capped like the server's tally. */
const MAX_ENTITIES = 50;
function countEntity(entry: SessionActivityEntry, id: string): void {
  const entities = entry.entities ?? {};
  if (entities[id] === undefined && Object.keys(entities).length >= MAX_ENTITIES) return;
  entities[id] = (entities[id] ?? 0) + 1;
  entry.entities = entities;
}

/** Fold a closed view's dwell into its row. Clicks never carry dwell. */
function tallyDwell(ev: UserActionEvent): void {
  if (!session?.activity || ev.dwellMs === undefined) return;
  const found = session.activity.find(e => sameSurface(e, ev.kind, ev.page, ev.screen, targetOf(ev)));
  if (found) found.dwellMs = (found.dwellMs ?? 0) + ev.dwellMs;
}

// ── Event recording ─────────────────────────────────────────────────────

function closeOpenEvent(atMs: number): void {
  if (!openEvent) return;
  openEvent.dwellMs = Math.max(0, atMs - openedAtMs);
  tallyDwell(openEvent);
  buffer.push(openEvent);
  openEvent = null;
}

function record(kind: UserActionKind, page: string, screen?: string, path?: string, props?: UserActionProps): void {
  if (!active || !userId) return;

  ensureSession(userId);
  const at = Date.now();

  if (kind === 'click') {
    // Clicks don't supersede the current view, so they don't close it — they
    // are interleaved into the buffer directly and carry no dwell.
    const ev: UserActionEvent = {
      seq: take('nextSeq'), t: nowIso(), kind, page,
      ...(screen ? { screen } : {}),
      ...(props ? { props } : {}),
    };
    tallyEvent(ev);
    buffer.push(ev);
  } else {
    closeOpenEvent(at);
    openEvent = {
      seq: take('nextSeq'),
      t: nowIso(),
      kind,
      page,
      ...(screen ? { screen } : {}),
      ...(path && path !== page ? { path } : {}),
      ...(props ? { props } : {}),
    };
    tallyEvent(openEvent);
    openedAtMs = at;
  }

  touchActivity();
  if (buffer.length >= FLUSH_AT_EVENTS) void flush();
}

// ── Transport ───────────────────────────────────────────────────────────

function describeDevice() {
  const w = window.innerWidth;
  return {
    deviceClass: (w < 640 ? 'phone' : w < 1024 ? 'tablet' : 'desktop') as 'phone' | 'tablet' | 'desktop',
    innerWidth: w,
    innerHeight: window.innerHeight,
    dpr: window.devicePixelRatio,
  };
}

async function send(batch: UserActionBatch): Promise<void> {
  if (!config) return;
  try {
    const res = await fetch(`${config.apiBase}/events/user-actions`, {
      method: 'POST',
      headers: { ...config.getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(batch),
      keepalive: true,
    });
    // The gate is authoritative server-side. A `false` here means it was turned
    // off under us — stop recording rather than keep posting into the void.
    if (res.ok) {
      const body = await res.json().catch(() => null) as { data?: { enabled?: boolean } } | null;
      if (body?.data?.enabled === false) stop();
    }
  } catch {
    /* swallow — instrumentation must never surface an error to the user */
  }
}

/**
 * Ship the closed events. `closeCurrent` also closes the event the user is on
 * (used when the page is going away and there may be no later chance).
 */
export function flush(opts: { closeCurrent?: boolean; endedReason?: SessionEndedReason } = {}): Promise<void> {
  if (!active || !session || !config) return Promise.resolve();
  if (opts.closeCurrent) closeOpenEvent(Date.now());
  if (buffer.length === 0) return Promise.resolve();
  if (peekCounter('nextBatchSeq') >= MAX_BATCHES) {
    buffer = [];
    return Promise.resolve();
  }

  const events = buffer;
  buffer = [];
  const batchSeq = take('nextBatchSeq');

  const batch: UserActionBatch = {
    sessionId: session.id,
    startedAt: session.startedAt,
    appSource: config.appSource,
    batchSeq,
    events,
    // Session-to-date, not per-batch: the server sets it wholesale, so a lost
    // or retried batch can't skew the totals.
    ...(session.activity?.length ? { activity: session.activity } : {}),
    ...(session.activityTruncated ? { activityTruncated: true } : {}),
    ...(opts.endedReason ? { endedReason: opts.endedReason } : {}),
    // The session header rides the sitting's first batch only.
    ...(batchSeq > 0
      ? {}
      : {
        session: {
          entryPage: events[0]?.page,
          device: describeDevice(),
          userAgent: navigator.userAgent,
          ...(config.appVersion ? { appVersion: config.appVersion } : {}),
        },
      }),
  };
  return send(batch);
}

/** Close out the current session — flushes everything with a reason attached. */
function endSession(reason: SessionEndedReason): void {
  if (session && active) void flush({ closeCurrent: true, endedReason: reason });
  discardSession();
}

/** Drop the session without shipping it. See the identity-change note above. */
function discardSession(): void {
  setSession(null);
  buffer = [];
  openEvent = null;
  clearState();
}

// ── Lifecycle listeners ─────────────────────────────────────────────────

function onVisibilityChange(): void {
  if (document.visibilityState === 'hidden') {
    void flush({ closeCurrent: true });
  } else if (openEvent === null && currentPage) {
    // Coming back — re-open the page the user returned to so the time after
    // their return is attributed rather than silently lost.
    record('page_view', currentPage);
  }
}

function onPageHide(): void {
  void flush({ closeCurrent: true });
}

/**
 * Delegated click capture. Only elements we deliberately marked with
 * `data-user-action` are recorded — never element text, never a blanket capture of
 * every click on the page. See the Privacy note in the design doc.
 *
 * Capture phase, because plenty of handlers call stopPropagation() and a
 * bubble-phase listener on document would silently miss exactly the primary
 * actions we most want to see.
 */
function onClick(e: Event): void {
  const start = e.target;
  if (!(start instanceof Element)) return;
  const el = start.closest('[data-user-action]');
  const target = el?.getAttribute('data-user-action');
  if (!target) return;
  recordClick(target.slice(0, MAX_ACTION_TARGET_CHARS));
}

function bindListeners(): void {
  if (listenersBound) return;
  document.addEventListener('visibilitychange', onVisibilityChange);
  document.addEventListener('click', onClick, true);
  window.addEventListener('pagehide', onPageHide);
  flushTimer = setInterval(() => void flush(), FLUSH_INTERVAL_MS);
  listenersBound = true;
}

function unbindListeners(): void {
  if (!listenersBound) return;
  document.removeEventListener('visibilitychange', onVisibilityChange);
  document.removeEventListener('click', onClick, true);
  window.removeEventListener('pagehide', onPageHide);
  if (flushTimer) clearInterval(flushTimer);
  flushTimer = null;
  listenersBound = false;
}

// ── Public API ──────────────────────────────────────────────────────────

/**
 * Begin tracing for a user. Idempotent — safe to call on every render.
 * The caller is responsible for the feature-gate check.
 */
export function start(uid: string, cfg: UserActionConfig): void {
  if (active && userId === uid) return;
  config = cfg;
  userId = uid;
  active = true;
  ensureSession(uid);
  bindListeners();
}

/** Stop recording and drop anything buffered. Used by the server kill switch. */
export function stop(): void {
  active = false;
  userId = null;
  setSession(null);
  buffer = [];
  openEvent = null;
  currentPage = '';
  unbindListeners();
}

/**
 * Close the current session at an identity boundary (login / logout /
 * impersonate) so a shared device never merges two people into one trace.
 */
export function resetSession(reason: SessionEndedReason = 'auth'): void {
  endSession(reason);
  currentPage = '';
}

export function recordPageView(page: string, path?: string, props?: UserActionProps): void {
  currentPage = page;
  record('page_view', page, undefined, path, props);
}

export function recordScreenView(screen: string, props?: UserActionProps): void {
  if (!currentPage) return;
  record('screen_view', currentPage, screen, undefined, props);
}

export function recordClick(target: string, props?: UserActionProps): void {
  if (!currentPage) return;
  record('click', currentPage, undefined, undefined, { target, ...props });
}

export function isActive(): boolean {
  return active;
}

/**
 * Test seam — resets every module-level value.
 *
 * `keepStorage` leaves the persisted session behind, which is what a page
 * reload actually looks like: fresh module state, same localStorage.
 */
export function __resetForTests(opts: { keepStorage?: boolean } = {}): void {
  unbindListeners();
  config = null;
  userId = null;
  active = false;
  setSession(null);
  buffer = [];
  openEvent = null;
  openedAtMs = 0;
  currentPage = '';
  if (!opts.keepStorage) clearState();
}

/** Test seam — inspect what would be flushed. */
export function __peek() {
  return {
    session,
    buffer: [...buffer],
    openEvent,
    seq: session?.nextSeq ?? 0,
    batchSeq: session?.nextBatchSeq ?? 0,
    active,
    currentPage,
    activity: session?.activity ? [...session.activity] : [],
  };
}
